import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, MoreHorizontal, Search } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
export interface RowAction<T> {
  label: string;
  icon: React.ElementType;
  onClick: (row: T) => void;
  danger?: boolean;
}
export interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (value: unknown, row: T) => ReactNode;
  sortable?: boolean;
  width?: string;
}
interface DataTableProps<T extends Record<string, unknown>> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  className?: string;
  rowActions?: RowAction<T>[];
  /** Enable built-in search filtering */
  searchable?: boolean;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Keys to search across (defaults to all string/number column keys) */
  searchKeys?: (keyof T)[];
}
export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  emptyMessage = 'Geen gegevens beschikbaar',
  className = '',
  rowActions,
  searchable = false,
  searchPlaceholder = 'Zoeken...',
  searchKeys
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  // Filter data by search query
  const filteredData = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return data;
    const query = searchQuery.toLowerCase();
    const keys = searchKeys || columns.map(c => String(c.key));
    return data.filter(row => keys.some(key => {
      const val = row[String(key)];
      if (val == null) return false;
      return String(val).toLowerCase().includes(query);
    }));
  }, [data, searchable, searchQuery, searchKeys, columns]);
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortKey) return 0;
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (aVal === bVal) return 0;
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    const cmp = typeof aVal === 'number' && typeof bVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal), 'nl');
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${className}`} data-id="element-5782">
      
      {searchable && <div className="px-4 py-3 border-b border-gray-100" data-id="element-5783">
          <div className="w-full sm:max-w-sm" data-id="element-5784">
            <Input type="text" placeholder={searchPlaceholder} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-5786" />} size="sm" data-id="element-5785" />
          
          </div>
          {searchQuery && <div className="text-xs text-gray-400 mt-1.5" data-id="element-5787">
              {filteredData.length} van {data.length} resultaten
            </div>}
        </div>}

      <div className="overflow-x-auto relative shadow-[inset_-12px_0_8px_-12px_rgba(0,0,0,0.1)] sm:shadow-none" data-id="element-5788">
        <table className="w-full text-sm min-w-[640px]" data-id="element-5789">
          <thead data-id="element-5790">
            <tr className="bg-gray-50 border-b border-gray-100" data-id="element-5791">
              {columns.map(col => <th key={String(col.key)} className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide ${col.sortable !== false ? 'cursor-pointer hover:text-gray-700 select-none' : ''}`} style={col.width ? {
              width: col.width
            } : undefined} onClick={() => col.sortable !== false && handleSort(String(col.key))} data-id="element-5792">
                
                  <div className="flex items-center gap-1" data-id="element-5793">
                    {col.label}
                    {col.sortable !== false && sortKey === String(col.key) && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" data-id="element-5794" /> : <ChevronDown className="w-3 h-3" data-id="element-5795" />)}
                  </div>
                </th>)}
              {rowActions && rowActions.length > 0 && <th className="px-4 py-3 w-10" data-id="element-5796"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50" data-id="element-5797">
            {sortedData.length === 0 ? <tr data-id="element-5798">
                <td colSpan={columns.length + (rowActions ? 1 : 0)} className="px-4 py-12 text-center text-sm text-gray-400" data-id="element-5799">
                
                  {emptyMessage}
                </td>
              </tr> : sortedData.map((row, i) => {
            const rowId = String(row.id ?? i);
            return <tr key={rowId} onClick={() => onRowClick?.(row)} className={`transition-colors ${onRowClick ? 'cursor-pointer hover:bg-blue-50/50' : 'hover:bg-gray-50'}`} data-id="element-5800">
                  
                    {columns.map(col => <td key={String(col.key)} className="px-4 py-3" data-id="element-5801">
                        {col.render ? col.render(row[String(col.key)], row) : <span className="text-gray-700" data-id="element-5802">
                            {String(row[String(col.key)] ?? '—')}
                          </span>}
                      </td>)}
                    {rowActions && rowActions.length > 0 && <td className="px-4 py-3 text-right relative" onClick={e => e.stopPropagation()} data-id="element-5803">
                    
                        <button onClick={() => setOpenMenuId(openMenuId === rowId ? null : rowId)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-5804">
                      
                          <MoreHorizontal className="w-4 h-4" data-id="element-5805" />
                        </button>

                        {openMenuId === rowId && <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} data-id="element-5806" />
                      
                            <div className="absolute right-0 sm:right-8 top-10 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20" data-id="element-5807">
                              {rowActions.map((action, idx) => {
                      const Icon = action.icon;
                      return <button key={idx} onClick={() => {
                        setOpenMenuId(null);
                        action.onClick(row);
                      }} className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left transition-colors ${action.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-5808">
                              
                                    <Icon className="w-4 h-4" data-id="element-5809" />
                                    {action.label}
                                  </button>;
                    })}
                            </div>
                          </>}
                      </td>}
                  </tr>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}